//
// # Parameters
//

// Local Imports
import { exhaust, Result } from "../errors";
import { schema } from "GenAlphaSchema";

// Schema re-exports
export type InstanceParam = schema.schematic.InstanceParam;
export type InstanceParamTuple = schema.schematic.InstanceParamTuple;
export type InstanceParamObject = schema.schematic.InstanceParamObject;
export type ParamKind = schema.schematic.ParamKind;

export const InstanceParam = {
  new: (
    name: string, // Parameter Name
    value: string, // Stringified Value
    kind?: ParamKind // Explicit Type. Defaults to string
  ): InstanceParamObject => {
    return { name, value, kind };
  },

  toObject: (p: InstanceParam): InstanceParamObject => {
    if (!Array.isArray(p)) return p; // Already an object, return as-is.
    // This is a tuple, destructure and return as an object.
    const [name, value, kind] = p;
    return { name, value, kind };
  },

  toTuple: (p: InstanceParam): InstanceParamTuple => {
    if (Array.isArray(p)) return p; // Already a tuple, return as-is.
    // This is an object, destructure and return as a tuple.
    const { name, value, kind } = p;
    if (kind && kind !== "string") return [name, value, kind];
    return [name, value];
  },

  validate: (p: InstanceParam): Result<TypedParam> => {
    const obj = InstanceParam.toObject(p);
    return TypedParam.validate(obj);
  },
};

// Typed parameter value
export type TypedParamValue =
  | { kind: "bool"; bool: boolean }
  | { kind: "float"; float: number }
  | { kind: "int"; int: number }
  | { kind: "string"; string: string };

export const TypedParamValue = {
  //
  // Validate the "value parts" of a schema param.
  validate: (s: {
    value: string;
    kind?: ParamKind;
  }): Result<TypedParamValue> => {
    const { value, kind } = s;

    // FIXME: wrap conversions which can fail, e.g. `Number(value)`
    switch (kind) {
      case undefined: // Unspecific case defaults to string
      case "string":
        return Result.Ok({ kind: "string", string: value });
      case "bool":
        return Result.Ok({ kind: "bool", bool: value === "true" });
      case "float":
        return Result.Ok({ kind: "float", float: Number(value) });
      case "int":
        return Result.Ok({ kind: "int", int: Number(value) });
      default:
        exhaust(kind);
    }
  },
};

// # Typed Param
export interface TypedParam {
  name: string; // Parameter Name
  value?: TypedParamValue; // Parameter Value
}
export const TypedParam = {
  validate: (obj: InstanceParamObject): Result<TypedParam> => {
    const r = TypedParamValue.validate(obj);
    if (r.err) return r;
    const { val } = r;
    return Result.Ok({ name: obj.name, value: val });
  },
};
